import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, ChevronRight, Check, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useDealershipData } from '@/data/hooks/useDealershipData';
import { useDealershipSettings } from '@/data/hooks/useDealershipSettings';
import { scoreDealPriority, REASON_LABEL } from '@/lib/dealPriority';
import { NEXT_STEP_LABELS } from '@/types';
import type { NextStep, ActivityType, Deal } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { timeAgo } from '@/data/mockData';

const NEXT_TO_ACTIVITY: Record<NextStep, ActivityType> = {
  call: 'text_sent',
  follow_up: 'note_added',
  appointment: 'appointment_set',
  send_numbers: 'quote_review',
  trade_review: 'walkaround',
  close: 'quote_review',
  sold: 'note_added',
  delivery: 'note_added',
};

export function NextBestActionCard() {
  const navigate = useNavigate();
  const { mode, userId, dealershipId } = useAuth();
  const { deals, refresh } = useDealershipData();
  const { settings } = useDealershipSettings();
  const [completing, setCompleting] = useState(false);

  const repId = mode === 'demo' ? 'rep-alex' : userId;

  const top = useMemo(() => {
    const myDeals = deals.filter((d) => d.rep_id === repId && d.stage !== 'sold');
    const ranked = myDeals
      .map((d) => ({ deal: d, r: scoreDealPriority(d, { staleHours: settings.stale_deal_hours || 72 }) }))
      .filter((x) => x.r.reason !== 'normal')
      .sort((a, b) => b.r.score - a.r.score);
    return ranked[0] ?? null;
  }, [deals, repId, settings.stale_deal_hours]);

  if (!top) {
    return (
      <Card className="border-green-500/20 bg-green-500/5">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-500/15 text-green-400 flex items-center justify-center">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-semibold">You're caught up</p>
            <p className="text-xs text-muted-foreground">Capture the next up when they walk in.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { deal, r } = top;
  const action = deal.next_step ? `Log ${NEXT_STEP_LABELS[deal.next_step]}` : 'Set next step';

  async function markDone(d: Deal) {
    if (!d.next_step) { navigate(`/deals/${d.id}`); return; }
    if (mode === 'demo') {
      toast.success(`Logged: ${NEXT_STEP_LABELS[d.next_step]} (demo)`);
      return;
    }
    if (!dealershipId || !userId) return;
    setCompleting(true);
    const { error: actErr } = await supabase.from('activities').insert({
      dealership_id: dealershipId,
      deal_id: d.id,
      rep_id: userId,
      activity_type: NEXT_TO_ACTIVITY[d.next_step],
      details: `Completed: ${NEXT_STEP_LABELS[d.next_step]}`,
      customer_name: d.customer_name,
    });
    if (actErr) {
      setCompleting(false);
      toast.error('Could not log', { description: actErr.message });
      return;
    }
    const { error: dealErr } = await supabase
      .from('deals')
      .update({ next_step: null, last_activity_at: new Date().toISOString() })
      .eq('id', d.id);
    setCompleting(false);
    if (dealErr) toast.error('Logged, but next step not cleared');
    else toast.success('Done. Set the next step.', {
      action: { label: 'Open', onClick: () => navigate(`/deals/${d.id}`) },
    });
    refresh();
  }

  return (
    <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest font-bold text-primary flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Next Best Action
          </span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {REASON_LABEL[r.reason]}
          </span>
        </div>
        <div>
          <p className="text-base font-bold">{deal.customer_name}</p>
          <p className="text-xs text-muted-foreground">{deal.vehicle} · last touched {timeAgo(deal.last_activity_at)}</p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            className="flex-1 gap-1.5"
            onClick={() => markDone(deal)}
            disabled={completing}
          >
            {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {action}
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate(`/deals/${deal.id}`)}>
            Open <ChevronRight className="h-4 w-4 ml-0.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}